import {
  camelToSnakeCaseKeys,
  fieldToOptionDeleted,
  getOptionValue,
  namedToOptionDeleted,
  emailToOptionDeleted,
  getOptionName,
} from 'services/helpers';

import {
  checkDeleted,
  displayNameDescriptor,
  nameDescriptor,
  getStageName,
  validStageId,
  isBeforeAfter,
  isDateAndTime,
} from 'pages/AutomationEngine/helpers';

import { apiVariableToOption } from 'pages/AutomationEngine/dialogs/ActionWizard/helpers';

import {
  AUTOMATION_STEPS,
  edgeLabels,
  triggerDisplay,
} from 'pages/AutomationEngine/steps';

import { addLoopDepthToSteps } from './loop-depth';

import { isGroupFilter, isGroupInvalid } from 'ts-filters/legacy';

import {
  activityLoggedGoalDTO,
  scheduledActivityOverdueDTO,
  contactTagAddedRemovedDTO,
  emailReceivedGoalDTO,
  fieldUpdatedGoalDTO,
  onOrAroundDateDTO,
  formSubmittedGoalDTO,
  surveySubmittedGoalDTO,
  emailLinkClickedDTO,
  stageUpdatedGoalDTO,
  scheduleDTO,
  webhookDTO,
  newEntityCreatedDTO,
} from 'pages/AutomationEngine/dialogs/GoalAndTriggerWizard/subsections/dto';

import {
  assignTeamMemberDTO,
  changeFieldValueDTO,
  changeTagsDTO,
  createRelatedEntityDTO,
  goToAutomationStepDTO,
  httpRequestDTO,
  mathOperatorDTO,
  startAutomationDTO,
  modifyAutomationDTO,
  archiveRecordDTO,
  searchRecordsDTO,
  modifyRelatedEntitiesDTO,
  notifyMemberViaEmailDTO,
  notifyMemberViaTextDTO,
  scheduleActivityDTO,
  deleteScheduledActivityDTO,
  sendEmailDTO,
  sendRelatedContactEmailDTO,
  updateVariableDTO,
  codeStepDTO,
  callLlmDTO,
  fileContentExtractionDTO,
  audioTranscriptionDTO,
  stopExecutionDTO,
  exitLoopDTO,
  sendTextDTO,
  sendRelatedContactTextDTO,
} from 'pages/AutomationEngine/dialogs/ActionWizard/subsections/dto';

import {
  stepLoopDTO,
  loopNextDTO,
} from 'pages/AutomationEngine/dialogs/LoopWizard/subsections/dto';

import { isVariableInitialization } from 'pages/AutomationEngine/dialogs/VariableWizard/utilities';

import { RECEIVING_INBOX } from 'pages/AutomationEngine/helpers';

import { BranchStepTypes } from '../types';

// dto functions for delay and conditional so we can use a look up insted a bunch of if statements
const delayDTO = {
  forApi: (step) => {
    const { daysSpecificTime, minutes, hours, days, variable, time, ...rest } =
      step.details;
    return {
      details: {
        ...rest,
        minutes: minutes || 0,
        hours: hours || 0,
        days: days || 0,
        ...(variable ? { variable: variable.value } : null),
        ...(time ? { time } : undefined),
      },
    };
  },
  forFlow: (step, addErrorMessage, messageDictionary) => {
    const { variable, ...rest } = step;
    const stepDelay = {
      ...rest,
      ...{ variable: variable ? apiVariableToOption(variable) : null },
    };

    return stepDelay;
  },
};

const mergeBranchesDTO = {
  forApi: (step) => {
    return step;
  },
  forFlow: (step) => {
    return step;
  },
};

const branchDTO = {
  forApi: (step) => {
    return step;
  },
  forFlow: (step) => {
    return step;
  },
};

const noopDTO = {
  forApi: (step) => {
    return step;
  },
  forFlow: (step) => {
    return step;
  },
};

import { variableForApi } from 'pages/AutomationEngine/dialogs/VariableWizard/variable-types/variableDTO';
import { isLLMDecisionFilter } from '__components/Wizards/MetaFilters/types';
import {
  createMergeSplitStepData,
  createBranchSplitStepData,
  isGoalOrCondition,
  isBranchStep,
} from './ts-utilities';
import { isConditionOrGoalSplit, isBranchSplit } from './ts-utilities';

export const preProcessDescriptionQues = ({
  automation,
  descriptionFallback,
}) => {
  const steps = automation.steps.reduce(
    (
      { steps, conditionIds, goalIds, delayIds, actionIds, variableIds },
      step
    ) => {
      const { stepCondition } = step;
      if (step.stepType === 'goal') {
        return {
          steps: [...steps, step],
          conditionIds,
          goalIds: [...goalIds, step.id],
          delayIds,
          actionIds,
          variableIds,
        };
      }
      if (step.stepType === 'delay') {
        return {
          steps: [...steps, step],
          conditionIds,
          goalIds,
          delayIds: [...delayIds, step.id],
          actionIds,
          variableIds,
        };
      }
      // variable
      if (isVariableInitialization(step.stepType)) {
        return {
          steps: [...steps, step],
          conditionIds,
          goalIds,
          delayIds,
          actionIds,
          variableIds: [...variableIds, step.id],
        };
      }
      if (step.stepType !== 'condition') {
        // actions
        return {
          steps: [...steps, step],
          conditionIds,
          goalIds,
          delayIds,
          actionIds: [...actionIds, step.id],
          variableIds,
        };
      }
      // custom filter
      if (stepCondition?.filterConfig) {
        const { filterConfig } = stepCondition;

        const query = filterConfig.query.map((q, qIndex) => {
          const filters = q.filters.map(({ description, ...rest }, fIndex) => {
            return {
              ...rest,
              description:
                description ??
                (qIndex === 0 && fIndex === 0 ? descriptionFallback : ''),
            };
          });
          return { ...q, filters };
        });

        return {
          steps: [
            ...steps,

            {
              ...step,
              stepCondition: {
                ...stepCondition,
                filterConfig: { ...filterConfig, query },
              },
            },
          ],
          conditionIds: [...conditionIds, step.id],
          goalIds,
          delayIds,
          actionIds,
          variableIds,
        };
      }

      // groups
      return {
        steps: [...steps, step],
        conditionIds: [...conditionIds, step.id],
        goalIds,
        delayIds,
        actionIds,
        variableIds,
      };
    },
    {
      steps: [],
      conditionIds: [],
      goalIds: [],
      delayIds: [],
      actionIds: [],
      variableIds: [],
    } // initial value
  );

  return {
    automationData: { ...automation, steps: steps.steps },
    descriptionQueIds: {
      conditionIds: steps.conditionIds,
      goalIds: steps.goalIds,
      delayIds: steps.delayIds,
      actionIds: steps.actionIds,
      variableIds: steps.variableIds,
    },
  };
};

const conditionDTO = {
  forApi: (step) => {
    const { groups, filterConfig, ...rest } = {
      ...step.details,
    };
    const groupIds = (groups || []).map(({ id }) => id);
    return {
      details: {
        ...rest,
        ...(groupIds.length ? { groupIds } : null),
        ...(filterConfig ? { filterConfig } : null),
      },
    };
  },
  forFlow: (orgData, addErrorMessage, messageDictionary, model) => {
    let { type, groups, filterErrors, filterConfig, llmDecision } = orgData;

    if (isGroupFilter(type)) {
      // we need to make sure group goes down this path error or not
      if (isGroupInvalid(groups)) {
        addErrorMessage(
          messageDictionary.thereWasanErrorLoadingTheAssociatedGroup
        );
      }
    } else if (isLLMDecisionFilter(type)) {
      if (!llmDecision?.modelName || !llmDecision?.businessPluginApp) {
        addErrorMessage(messageDictionary.llmPluginAppIsMissing);
      } else {
        const { businessPluginApp, ...rest } = llmDecision;
        llmDecision = {
          ...rest,
          businessPluginAppId: llmDecision.businessPluginApp.id,
        };
      }
    } else {
      // query keys need to be snake_case
      const query = camelToSnakeCaseKeys(filterConfig.query);

      filterConfig = { ...filterConfig, query };

      if (filterErrors || filterConfig?.invalid) {
        addErrorMessage(
          messageDictionary.thereWasanErrorLoadingTheAssociatedFilters
        );
      }
    }
    return {
      ...orgData,
      filterConfig,
      llmDecision,
    };
  },
};
/*-------------------------------------------------------------------------------------------------

 Take the step from the api and transform for the flow

 the object name comes from the api but we match it by what's defined in the steps.js file
  i.e. api === math_operator {action|step} MathOperator

 */
const goalsForFlowList = {
  [AUTOMATION_STEPS.activity_logged.type]: activityLoggedGoalDTO.forFlow,
  [AUTOMATION_STEPS.scheduled_activity_overdue.type]:
    scheduledActivityOverdueDTO.forFlow,
  [AUTOMATION_STEPS.contact_tag_added_removed.type]:
    contactTagAddedRemovedDTO.forFlow,
  [AUTOMATION_STEPS.field_updated.type]: fieldUpdatedGoalDTO.forFlow,
  [AUTOMATION_STEPS.email_received.type]: emailReceivedGoalDTO.forFlow,
  [AUTOMATION_STEPS.on_or_around_date.type]: onOrAroundDateDTO.forFlow,
  [AUTOMATION_STEPS.form_submitted.type]: formSubmittedGoalDTO.forFlow,
  [AUTOMATION_STEPS.survey_submitted.type]: surveySubmittedGoalDTO.forFlow,
  [AUTOMATION_STEPS.email_link_clicked.type]: emailLinkClickedDTO.forFlow,
  [AUTOMATION_STEPS.stage_updated.type]: stageUpdatedGoalDTO.forFlow,
  [AUTOMATION_STEPS.schedule.type]: scheduleDTO.forFlow,
  [AUTOMATION_STEPS.webhook.type]: webhookDTO.forFlow,
  [AUTOMATION_STEPS.new_entity_created.type]: newEntityCreatedDTO.forFlow,
};

export const stepForFlow = (
  apiStep,
  model,
  messageDictionary,
  automationPluginKeys,
  automationPlugins
) => {
  // get the action||step objects as they comes from the api
  // add any new actions here !
  const {
    id,
    parentStepId,
    stepType,
    actionAssignTeamMember,
    actionChangeFieldValue,
    actionChangeTags,
    actionSendEmail,
    actionStartCancelAutomation,
    actionNotifyMemberViaEmail,
    actionNotifyMemberViaText,
    actionScheduleActivity,
    actionDeleteScheduledActivity,
    actionGoToAutomationStep,
    actionMathOperator,
    actionHttpRequest,
    actionStartAutomation,
    actionModifyAutomation,
    actionArchiveRecord,
    actionSearchRecords,
    actionModifyRelatedEntities,
    actionSendRelatedContactEmail,
    actionCreateRelatedEntity,
    actionUpdateVariable,
    actionCodeStep,
    actionCallLlm,
    actionFileContentExtraction,
    actionAudioTranscription,
    actionStopExecution,
    actionExitLoop,
    actionSendText,
    actionSendRelatedContactText,
    actionPluginCodeStep,
    stepCondition,
    stepDelay,
    stepGoal,
    stepLoop,

    order,
    ...others
  } = apiStep;

  const errorList = [];
  const addErrorMessage = (errorMessage) => {
    errorList.push(errorMessage);
  };
  try {
    if (stepGoal) {
      const { triggers } = stepGoal;

      // using only first trigger for now
      const firstTrigger = triggers[0];
      delete stepGoal.triggers;
      apiStep.stepGoal.type = 'goal';
      const variable = stepGoal.variable
        ? apiVariableToOption(stepGoal.variable)
        : null;

      Object.assign(stepGoal, {
        ...goalsForFlowList[firstTrigger.triggerType](
          firstTrigger,
          addErrorMessage,
          messageDictionary,
          model
        ),
        variable,
      });

      // end of goals
    }
  } catch (error) {
    addErrorMessage(messageDictionary.automationFailedToLoad);
  }
  const details = {
    ...((actionChangeFieldValue &&
      changeFieldValueDTO.forFlow(
        actionChangeFieldValue,
        addErrorMessage,
        messageDictionary
      )) ||
      (actionChangeTags &&
        changeTagsDTO.forFlow(
          actionChangeTags,
          addErrorMessage,
          messageDictionary
        )) ||
      (actionSendEmail &&
        sendEmailDTO.forFlow(
          actionSendEmail,
          addErrorMessage,
          messageDictionary
        )) ||
      actionStartCancelAutomation ||
      (actionNotifyMemberViaEmail &&
        notifyMemberViaEmailDTO.forFlow(
          actionNotifyMemberViaEmail,
          addErrorMessage,
          messageDictionary
        )) ||
      (actionNotifyMemberViaText &&
        notifyMemberViaTextDTO.forFlow(
          actionNotifyMemberViaText,
          addErrorMessage,
          messageDictionary
        )) ||
      (actionScheduleActivity &&
        scheduleActivityDTO.forFlow(
          actionScheduleActivity,
          addErrorMessage,
          messageDictionary
        )) ||
      (actionDeleteScheduledActivity &&
        deleteScheduledActivityDTO.forFlow(
          actionDeleteScheduledActivity,
          addErrorMessage,
          messageDictionary
        )) ||
      (actionAssignTeamMember &&
        assignTeamMemberDTO.forFlow(
          actionAssignTeamMember,
          addErrorMessage,
          messageDictionary,
          stepType
        )) ||
      (actionGoToAutomationStep &&
        goToAutomationStepDTO.forFlow(
          actionGoToAutomationStep,
          addErrorMessage,
          messageDictionary
        )) ||
      (actionModifyAutomation &&
        modifyAutomationDTO.forFlow(
          actionModifyAutomation,
          addErrorMessage,
          messageDictionary
        )) ||
      (stepCondition &&
        conditionDTO.forFlow(
          stepCondition,
          addErrorMessage,
          messageDictionary,
          model
        )) ||
      stepGoal ||
      (stepDelay &&
        delayDTO.forFlow(stepDelay, addErrorMessage, messageDictionary)) ||
      (actionMathOperator &&
        mathOperatorDTO.forFlow(
          actionMathOperator,
          addErrorMessage,
          messageDictionary
        )) ||
      (actionModifyRelatedEntities &&
        modifyRelatedEntitiesDTO.forFlow(
          actionModifyRelatedEntities,
          addErrorMessage,
          messageDictionary
        )) ||
      (actionSendRelatedContactEmail &&
        sendRelatedContactEmailDTO.forFlow(
          actionSendRelatedContactEmail,
          addErrorMessage,
          messageDictionary
        )) ||
      (actionStartAutomation &&
        startAutomationDTO.forFlow(
          actionStartAutomation,
          addErrorMessage,
          messageDictionary
        )) ||
      (actionArchiveRecord &&
        archiveRecordDTO.forFlow(
          actionArchiveRecord,
          addErrorMessage,
          messageDictionary
        )) ||
      (actionSearchRecords &&
        searchRecordsDTO.forFlow(
          actionSearchRecords,
          addErrorMessage,
          messageDictionary
        )) ||
      (actionCreateRelatedEntity &&
        createRelatedEntityDTO.forFlow(
          actionCreateRelatedEntity,
          addErrorMessage,
          messageDictionary
        )) ||
      (actionUpdateVariable &&
        updateVariableDTO.forFlow(
          actionUpdateVariable,
          addErrorMessage,
          messageDictionary,
          stepType
        )) ||
      (actionCodeStep &&
        codeStepDTO.forFlow(
          actionCodeStep,
          addErrorMessage,
          messageDictionary,
          stepType,
          automationPluginKeys,
          automationPlugins
        )) ||
      (actionPluginCodeStep &&
        codeStepDTO.forFlow(
          actionPluginCodeStep,
          addErrorMessage,
          messageDictionary,
          stepType,
          automationPluginKeys,
          automationPlugins
        )) ||
      (actionCallLlm &&
        callLlmDTO.forFlow(
          actionCallLlm,
          addErrorMessage,
          messageDictionary
        )) ||
      (actionFileContentExtraction &&
        fileContentExtractionDTO.forFlow(
          actionFileContentExtraction,
          addErrorMessage,
          messageDictionary
        )) ||
      (actionAudioTranscription &&
        audioTranscriptionDTO.forFlow(
          actionAudioTranscription,
          addErrorMessage,
          messageDictionary
        )) ||
      (actionStopExecution &&
        stopExecutionDTO.forFlow(
          actionStopExecution,
          addErrorMessage,
          messageDictionary
        )) ||
      (actionExitLoop && exitLoopDTO.forFlow(actionExitLoop)) ||
      (actionSendText &&
        sendTextDTO.forFlow(
          actionSendText,
          addErrorMessage,
          messageDictionary
        )) ||
      (actionSendRelatedContactText &&
        sendRelatedContactTextDTO.forFlow(
          actionSendRelatedContactText,
          addErrorMessage,
          messageDictionary
        )) ||
      (stepLoop &&
        stepLoopDTO.forFlow(stepLoop, addErrorMessage, messageDictionary))),
  };

  const config = AUTOMATION_STEPS[stepType];
  if (!config) {
    addErrorMessage(messageDictionary.deletedStep);
  }

  // all the action||step objects are added to the details
  return {
    step: {
      id,
      type: stepType,
      parentKey: parentStepId,
      fromLabel: '', // It is easier to work with labels colocated with parentKey than on the parent
      prefix: config?.prefix,
      goalType: config?.goalType || false,
      details,
      hasError: errorList.length ? errorList : false,
      isBranchGroupInitiator: others.isBranchGroupInitiator ?? false,
      isBranchMerged: false,
      mergeParent: null,
      loopParent:
        stepType === AUTOMATION_STEPS.next_or_finish.type ? parentStepId : null,
      ...others,
      ...(actionHttpRequest && {
        actionHttpRequest: httpRequestDTO.forFlow(actionHttpRequest),
      }),
    },
    error: errorList.length ? { id, errors: errorList } : null,
  };
};

/*-------------------------------------------------------------------------------------------------

 Take the step from the UI and transform for the api

 */

const goalForApi = {
  [AUTOMATION_STEPS.activity_logged_goal.type]: {
    forApi: activityLoggedGoalDTO.forApi,
    deleteGoalType: true,
  },
  [AUTOMATION_STEPS.scheduled_activity_overdue_goal.type]: {
    forApi: scheduledActivityOverdueDTO.forApi,
    deleteGoalType: true,
  },
  [AUTOMATION_STEPS.contact_tag_added_removed_goal.type]: {
    forApi: contactTagAddedRemovedDTO.forApi,
    deleteGoalType: false,
  },
  [AUTOMATION_STEPS.field_updated_goal.type]: {
    forApi: fieldUpdatedGoalDTO.forApi,
    deleteGoalType: false,
  },
  [AUTOMATION_STEPS.email_received_goal.type]: {
    forApi: emailReceivedGoalDTO.forApi,
    deleteGoalType: false,
  },
  [AUTOMATION_STEPS.on_or_around_date_goal.type]: {
    forApi: onOrAroundDateDTO.forApi,
    deleteGoalType: false,
  },
  [AUTOMATION_STEPS.form_submitted_goal.type]: {
    forApi: formSubmittedGoalDTO.forApi,
    deleteGoalType: false,
  },
  [AUTOMATION_STEPS.survey_submitted_goal.type]: {
    forApi: surveySubmittedGoalDTO.forApi,
    deleteGoalType: false,
  },
  [AUTOMATION_STEPS.email_link_clicked_goal.type]: {
    forApi: emailLinkClickedDTO.forApi,
    deleteGoalType: false,
  },
  [AUTOMATION_STEPS.stage_updated.type]: {
    forApi: stageUpdatedGoalDTO.forApi,
    deleteGoalType: false,
  },
  [AUTOMATION_STEPS.stage_updated_goal.type]: {
    forApi: stageUpdatedGoalDTO.forApi,
    deleteGoalType: false,
  },
  [AUTOMATION_STEPS.schedule.type]: {
    forApi: scheduleDTO.forApi,
    deleteGoalType: false,
  },
  [AUTOMATION_STEPS.webhook.type]: {
    forApi: webhookDTO.forApi,
    deleteGoalType: false,
  },
  [AUTOMATION_STEPS.new_entity_created.type]: {
    forApi: newEntityCreatedDTO.forApi,
    deleteGoalType: false,
  },
};

const actionForApi = {
  [AUTOMATION_STEPS.assign_team_member.type]: {
    forApi: assignTeamMemberDTO.forApi,
  },
  [AUTOMATION_STEPS.change_field_value.type]: {
    forApi: changeFieldValueDTO.forApi,
  },
  [AUTOMATION_STEPS.change_tags.type]: {
    forApi: changeTagsDTO.forApi,
  },
  [AUTOMATION_STEPS.create_related_entity.type]: {
    forApi: createRelatedEntityDTO.forApi,
  },
  [AUTOMATION_STEPS.go_to_automation_step.type]: {
    forApi: goToAutomationStepDTO.forApi,
  },
  [AUTOMATION_STEPS.http_request.type]: {
    forApi: httpRequestDTO.forApi,
  },
  [AUTOMATION_STEPS.math_operator.type]: {
    forApi: mathOperatorDTO.forApi,
  },
  [AUTOMATION_STEPS.modify_automation.type]: {
    forApi: modifyAutomationDTO.forApi,
  },
  [AUTOMATION_STEPS.start_automation.type]: {
    forApi: startAutomationDTO.forApi,
  },
  [AUTOMATION_STEPS.modify_related_entities.type]: {
    forApi: modifyRelatedEntitiesDTO.forApi,
  },
  [AUTOMATION_STEPS.archive_record.type]: {
    forApi: archiveRecordDTO.forApi,
  },
  [AUTOMATION_STEPS.search_records.type]: {
    forApi: searchRecordsDTO.forApi,
  },
  [AUTOMATION_STEPS.notify_member_via_email.type]: {
    forApi: notifyMemberViaEmailDTO.forApi,
  },
  [AUTOMATION_STEPS.notify_member_via_text.type]: {
    forApi: notifyMemberViaTextDTO.forApi,
  },
  [AUTOMATION_STEPS.schedule_activity.type]: {
    forApi: scheduleActivityDTO.forApi,
  },
  [AUTOMATION_STEPS.delete_scheduled_activity.type]: {
    forApi: deleteScheduledActivityDTO.forApi,
  },
  [AUTOMATION_STEPS.send_email.type]: {
    forApi: sendEmailDTO.forApi,
  },
  [AUTOMATION_STEPS.send_related_contact_email.type]: {
    forApi: sendRelatedContactEmailDTO.forApi,
  },
  [AUTOMATION_STEPS.update_variable.type]: {
    forApi: updateVariableDTO.forApi,
  },
  [AUTOMATION_STEPS.code_step.type]: {
    forApi: codeStepDTO.forApi,
  },
  [AUTOMATION_STEPS.plugin_code_step.type]: {
    forApi: codeStepDTO.forApi,
  },
  [AUTOMATION_STEPS.call_llm.type]: {
    forApi: callLlmDTO.forApi,
  },
  [AUTOMATION_STEPS.file_content_extraction.type]: {
    forApi: fileContentExtractionDTO.forApi,
  },
  [AUTOMATION_STEPS.audio_transcription.type]: {
    forApi: audioTranscriptionDTO.forApi,
  },
  [AUTOMATION_STEPS.stop_execution.type]: {
    forApi: stopExecutionDTO.forApi,
  },
  [AUTOMATION_STEPS.exit_loop.type]: {
    forApi: exitLoopDTO.forApi,
  },
  [AUTOMATION_STEPS.send_text.type]: {
    forApi: sendTextDTO.forApi,
  },
  [AUTOMATION_STEPS.send_related_contact_text.type]: {
    forApi: sendRelatedContactTextDTO.forApi,
  },
  // delay
  [AUTOMATION_STEPS.delay.type]: {
    forApi: delayDTO.forApi,
  },
  [AUTOMATION_STEPS.condition.type]: {
    forApi: conditionDTO.forApi,
  },
  // merge_branches
  [AUTOMATION_STEPS.merge_branches.type]: {
    forApi: mergeBranchesDTO.forApi,
  },
  // branch
  [AUTOMATION_STEPS.branch.type]: {
    forApi: branchDTO.forApi,
  },
  // noop
  [AUTOMATION_STEPS.noop.type]: {
    forApi: noopDTO.forApi,
  },
  // loops
  [AUTOMATION_STEPS.loop.type]: {
    forApi: stepLoopDTO.forApi,
  },
  [AUTOMATION_STEPS.next_or_finish.type]: {
    forApi: loopNextDTO.forApi,
  },
};

export const stepForApi =
  (firstIndex = 0) =>
  ({ parentKey, fromLabel, ...others }, order) => {
    const step = {
      ...others,
      order: order + firstIndex,
    };
    if (step.goalType) {
      // Goal section
      const details = {
        ...step.details,
        variable: step.details.variable
          ? getOptionName(step.details.variable)
          : null,
      };

      if (goalForApi[details.type].deleteGoalType) {
        delete step.goalType;
      }

      Object.assign(step, goalForApi[details.type].forApi(details));
    } else {
      // Actions section
      Object.assign(step, actionForApi[step.type].forApi(step));
    }

    const { details, id, loopParent, ...rest } = step;
    return {
      // we need id especially to update action and if it's not a new step
      ...(!id.startsWith('new') && {
        id,
      }),
      [`${step.prefix}_${step.type}`]: details,
      key: id,
      parentKey: loopParent || parentKey, // for the the loop_next step
      ...rest,
      parentYesNo: fromLabel,
      parentCondition: fromLabel,
    };
  };

/*-------------------------------------------------------------------------------------------------

 Take the trigger from the api and transform for the UI

 */

export const triggerForFlow = (trigger, model, messageDictionary) => {
  const {
    id,
    triggerType,
    triggerActivityLogged,
    triggerScheduledActivityOverdue,
    triggerEmailInteraction,
    triggerFieldUpdated,
    triggerFormSubmitted,
    triggerInteractionOccurred,
    triggerOnOrAroundDate,
    triggerSurveySubmitted,
    triggerContactTagAddedRemoved,
    triggerWebsiteVisited,
    triggerEmailReceivedFromContact,
    triggerEmailLinkClicked,
    triggerStageUpdated,
    triggerSchedule,
    triggerWebhook,
    triggerNewEntityCreated,
    ...others
  } = trigger;

  const errorList = [];
  const addErrorMessage = (errorMessage) => {
    errorList.push(errorMessage);
  };
  try {
    if (triggerContactTagAddedRemoved) {
      const { tag } = triggerContactTagAddedRemoved;
      if (!tag) {
        addErrorMessage(
          messageDictionary.thereWasanErrorLoadingTheAssociatedTag
        );
      } else {
        triggerContactTagAddedRemoved.tagId = namedToOptionDeleted(tag);
      }
    }

    if (triggerType === AUTOMATION_STEPS.field_updated.type) {
      Object.assign(
        triggerFieldUpdated,
        fieldUpdatedGoalDTO.triggerForFlow(
          triggerFieldUpdated,
          addErrorMessage,
          messageDictionary
        )
      );
    }

    if (triggerType === AUTOMATION_STEPS.form_submitted.type) {
      if (!triggerFormSubmitted) {
        addErrorMessage(messageDictionary.automationFailedToLoad);
      } else if (triggerFormSubmitted?.form) {
        const { form } = triggerFormSubmitted;
        triggerFormSubmitted.formId = namedToOptionDeleted(form);
        checkDeleted(messageDictionary, addErrorMessage, form, nameDescriptor);
      }
    }

    if (triggerActivityLogged?.activityType) {
      const { activityType } = triggerActivityLogged;
      triggerActivityLogged.activity = namedToOptionDeleted(activityType);
      checkDeleted(
        messageDictionary,
        addErrorMessage,
        activityType,
        nameDescriptor
      );
    }

    if (triggerScheduledActivityOverdue?.activity) {
      const { activity } = triggerScheduledActivityOverdue;
      triggerScheduledActivityOverdue.activity = namedToOptionDeleted(activity);
      checkDeleted(
        messageDictionary,
        addErrorMessage,
        activity,
        nameDescriptor
      );
    }

    if (triggerOnOrAroundDate) {
      const { field, period, dateOffsetDays } = triggerOnOrAroundDate;
      triggerOnOrAroundDate.fieldId = fieldToOptionDeleted(field);
      triggerOnOrAroundDate.period = period ? period.toUpperCase() : null;
      triggerOnOrAroundDate.dateOffsetDays = dateOffsetDays || null;

      checkDeleted(
        messageDictionary,
        addErrorMessage,
        field,
        displayNameDescriptor
      );
    }

    if (triggerType === AUTOMATION_STEPS.survey_submitted.type) {
      if (!triggerSurveySubmitted) {
        addErrorMessage(messageDictionary.automationFailedToLoad);
      } else if (triggerSurveySubmitted?.survey) {
        const { survey } = triggerSurveySubmitted;
        triggerSurveySubmitted.surveyId = namedToOptionDeleted(survey);
        checkDeleted(
          messageDictionary,
          addErrorMessage,
          survey,
          nameDescriptor
        );
      }
    }

    if (triggerType === AUTOMATION_STEPS.email_received.type) {
      if (!triggerEmailReceivedFromContact) {
        addErrorMessage(messageDictionary.automationFailedToLoad);
      } else {
        const { selectedIntegratedInboxes, receivingInbox } =
          triggerEmailReceivedFromContact;

        if (selectedIntegratedInboxes) {
          triggerEmailReceivedFromContact.selectedIntegratedInboxes =
            selectedIntegratedInboxes.map((inbox) =>
              emailToOptionDeleted(inbox)
            );

          if (
            triggerEmailReceivedFromContact.selectedIntegratedInboxes.length ===
              0 &&
            receivingInbox === RECEIVING_INBOX.selectedIntegratedInboxes
          ) {
            addErrorMessage(
              messageDictionary.thereWasanErrorLoadingTheAssociatedInbox
            );
          }
        }
      }
    }

    if (triggerStageUpdated) {
      const { fromStage, toStage } = triggerStageUpdated;
      if (!validStageId(fromStage, model) || !validStageId(toStage, model)) {
        addErrorMessage(
          messageDictionary.thereWasanErrorLoadingTheAssociatedStage
        );
      }
      triggerStageUpdated.fromStage = getStageName(
        fromStage,
        model,
        messageDictionary
      );
      triggerStageUpdated.toStage = getStageName(
        toStage,
        model,
        messageDictionary
      );
    }

    if (triggerSchedule) {
      Object.assign(triggerSchedule, scheduleDTO.forFlow(triggerSchedule));
    }

    if (triggerWebhook) {
      Object.assign(
        triggerWebhook,
        webhookDTO.forFlow(triggerWebhook, addErrorMessage, messageDictionary)
      );
    }
  } catch (error) {
    addErrorMessage(messageDictionary.automationFailedToLoad);
  }

  const config = AUTOMATION_STEPS[triggerType];
  if (!config) {
    addErrorMessage(messageDictionary.deletedStep);
  }
  return {
    trigger: {
      id,
      type: triggerType,
      prefix: config?.prefix || triggerDisplay.prefix,
      details: {
        ...(triggerActivityLogged ||
          triggerScheduledActivityOverdue ||
          triggerEmailInteraction ||
          triggerFieldUpdated ||
          triggerFormSubmitted ||
          triggerInteractionOccurred ||
          triggerOnOrAroundDate ||
          triggerSurveySubmitted ||
          triggerContactTagAddedRemoved ||
          triggerWebsiteVisited ||
          triggerEmailReceivedFromContact ||
          triggerEmailLinkClicked ||
          triggerStageUpdated ||
          triggerSchedule ||
          triggerWebhook ||
          triggerNewEntityCreated),
      },
      hasError: Boolean(errorList.length),
      ...others,
    },
    error: errorList.length ? { id, errors: errorList } : null,
  };
};

export const triggersForFlow = (apiTriggers, model, messageDictionary) => {
  const triggersWithError = orderSteps(apiTriggers).map((trigger) =>
    triggerForFlow(trigger, model, messageDictionary)
  );

  return {
    triggers: triggersWithError.map(({ trigger }) => ({ ...trigger })),
    triggerErrors: triggersWithError
      .filter(({ error }) => !!error)
      .map(({ error }) => error),
  };
};

export const triggerForApi = (trigger, order) => {
  const result = { ...trigger, order };

  if (trigger.type === AUTOMATION_STEPS.contact_tag_added_removed.type) {
    const { tagId, tagOperation } = result.details;
    result.details = {
      tagId: getOptionValue(tagId),
      tagOperation,
    };
  }
  if (trigger.type === AUTOMATION_STEPS.field_updated.type) {
    result.details = fieldUpdatedGoalDTO.triggerForApi(trigger.details);
  }
  if (trigger.type === AUTOMATION_STEPS.form_submitted.type) {
    result.details = {
      formId: getOptionValue(trigger.details.formId),
    };
  }
  if (trigger.type === AUTOMATION_STEPS.activity_logged.type) {
    result.details = {
      activityTypeId: getOptionValue(trigger.details.activity),
    };
  }
  if (trigger.type === AUTOMATION_STEPS.scheduled_activity_overdue.type) {
    result.details = {
      activity: { id: getOptionValue(trigger.details.activity) },
    };
  }
  if (trigger.type === AUTOMATION_STEPS.on_or_around_date.type) {
    const { fieldId, period, time, dateOffsetDays, ...other } = result.details;

    result.details = {
      ...other,
      fieldId: getOptionValue(fieldId),
      ...(!isDateAndTime(other.dateOffset)
        ? { time, period: period ? period.toLowerCase() : null }
        : null),
      ...(isBeforeAfter(other.dateOffset) ? { dateOffsetDays } : null),
    };
  }

  if (trigger.type === AUTOMATION_STEPS.survey_submitted.type) {
    result.details = {
      surveyId: getOptionValue(trigger.details.surveyId),
    };
  }

  if (trigger.type === AUTOMATION_STEPS.email_received.type) {
    const { receivingInbox, selectedIntegratedInboxes } = result.details;
    result.details = {
      receivingInbox,
      ...(selectedIntegratedInboxes && {
        selectedIntegratedInboxes: selectedIntegratedInboxes.map(
          (inbox) => inbox.value
        ),
      }),
    };
  }

  if (trigger.type === AUTOMATION_STEPS.stage_updated.type) {
    result.details = {
      fromStage: getOptionValue(trigger.details.fromStage),
      toStage: getOptionValue(trigger.details.toStage),
      fireOnCreate: trigger.details.fireOnCreate,
    };
  }

  if (trigger.type === AUTOMATION_STEPS.schedule.type) {
    result.details = scheduleDTO.forApi(trigger.details);
  }

  if (trigger.type === AUTOMATION_STEPS.webhook.type) {
    result.details = webhookDTO.forApi(trigger.details);
  }
  if (trigger.type === AUTOMATION_STEPS.new_entity_created.type) {
    result.details = {
      action: trigger.details?.action,
    };
  }

  const getType = () => {
    if (trigger.type === AUTOMATION_STEPS.email_received.type) {
      return `${trigger.type}_from_contact`;
    }

    return trigger.type;
  };

  const { details, id, ...rest } = result;
  return {
    // we need id especially to update action and if it's not a new trigger
    ...(!id.startsWith('new') && { id }),
    [`${trigger.prefix}_${getType()}`]: details,
    key: id,
    ...rest,
  };
};

export const automationForApi = ({
  automation: {
    id,
    name,
    active,
    revision,
    customObject,
    errorNotificationEmail,
    skipNonWorkingDays,
    folder,
    triggerThrottleSeconds,
    type,
  },
  steps: autSteps,
  triggers,
  variables,
}) => {
  // merge the variables into the steps
  let parentKey = null;
  let parentStepId = null;

  const _variables = variables.reduce(variableForApi, []).map((v, order) => {
    const ret = { ...v, parentKey, parentStepId, order };
    parentKey = v.key;
    parentStepId = v.id || null;
    return ret;
  });

  const parentKeyMap = autSteps.reduce((previousMap, step) => {
    if (!(isConditionOrGoalSplit(step) || isBranchSplit(step))) {
      return previousMap;
    }
    return { ...previousMap, [step.id]: step.parentKey };
  }, {});

  // link the first step to the end of the variables
  // filter out any placeholder steps
  const mappedSteps = autSteps
    .map((step) => {
      const parentKey = parentKeyMap[step.parentKey];
      return parentKey ? { ...step, parentKey } : step;
    })
    .filter((step) => !(isConditionOrGoalSplit(step) || isBranchSplit(step)))
    .map((s) => {
      return s.parentKey ? s : { ...s, parentKey };
    })
    .map(stepForApi(_variables.length));

  return {
    id, // Should be empty during creation
    name,
    active,
    lastRevision: revision, // Should be undefined for creation
    customObjectId: customObject?.id,
    returnAllStepsErrors: true,
    errorNotificationEmail,
    skipNonWorkingDays,
    steps: [..._variables, ...mappedSteps],
    triggers: triggers.map(triggerForApi),
    folder_id: folder?.id,
    triggerThrottleSeconds,
    type,
  };
};

// Maintaining the record order of steps and triggers helps to
// ensure that the ordering of branches in the automation flow
// don't change when the agentic workflow is saved. Note that this
// also works for triggers, not just steps.

export const orderSteps = (apiSteps) => {
  return [...apiSteps].sort((a, b) => {
    return (a.order || 0) - (b.order || 0);
  });
};

export { addLoopDepthToSteps };

export const stepsForFlow = (
  apiSteps,
  model,
  messageDictionary,
  automationPluginKeys,
  automationPlugins
) => {
  const labelsById = {};
  const stepsWithErrors = orderSteps(apiSteps).map((apiStep) => {
    // Map steps for flow
    const stepWithError = stepForFlow(
      apiStep,
      model,
      messageDictionary,
      automationPluginKeys,
      automationPlugins
    );
    Object.values(edgeLabels).forEach((label) => {
      // Keep track of edge labels
      []
        .concat(stepWithError.step.details[label.detailsKey] || [])
        .forEach((labeledStep) => {
          const labeledStepId =
            typeof labeledStep === 'string' ? labeledStep : labeledStep.id;
          labelsById[labeledStepId] = label.type;
        });
      delete stepWithError.step.details[label.detailsKey];
    });
    return stepWithError;
  });

  stepsWithErrors.forEach((stepWithError) => {
    // Mark steps with edge labels
    stepWithError.step.fromLabel =
      labelsById[stepWithError.step.id] || stepWithError.step.parentCondition;
  });

  // insert placeholder steps where needed
  // need to add is_branch_group_initiator check
  let parentKeyMap = {};

  const stepsWithMergeSplits = stepsWithErrors
    .map(({ step }) => ({ ...step }))
    .reduce((previousSteps, step) => {
      previousSteps.push(step);
      if (isGoalOrCondition(step)) {
        const mergeStep = createMergeSplitStepData(
          step.id,
          step.isBranchGroupInitiator,
          step.goalType,
          null,
          step.loopDepth,
          step.loopVariables
        );
        parentKeyMap = { ...parentKeyMap, [step.id]: mergeStep.id };
        previousSteps.push(mergeStep);
      } else if (isBranchStep(step)) {
        const mergeStep = createBranchSplitStepData(
          step.id,
          step.isBranchGroupInitiator,
          step.goalType,
          null,
          step.loopDepth,
          step.loopVariables
        );
        parentKeyMap = { ...parentKeyMap, [step.id]: mergeStep.id };
        previousSteps.push(mergeStep);
      }

      return previousSteps;
    }, []);

  const steps = !Object.keys(parentKeyMap).length
    ? stepsWithMergeSplits
    : stepsWithMergeSplits.map((step) => {
        const parentKey = parentKeyMap[step.parentKey];
        return parentKey &&
          step.id !== parentKey &&
          step.type !== BranchStepTypes.MERGE
          ? { ...step, parentKey }
          : step;
      });

  const loopSteps = addLoopDepthToSteps(steps);

  return {
    steps: loopSteps,
    stepErrors: stepsWithErrors
      .filter(({ error }) => !!error)
      .map(({ error }) => error),
  };
};

export const getBranchChildrenStep = (parentIds, fromLabel, steps) => {
  const children = steps.reduce((acc, item) => {
    if (
      parentIds.includes(item.parentKey) &&
      (!fromLabel || item.fromLabel === fromLabel)
    ) {
      return [...acc, item.id];
    }
    return acc;
  }, []);

  return children;
};

// Action steps that can't have children i.e. go to steps, can't be added in the tree where there can be no branchs i.e.
// after options when there are currently child steps of the option as these steps would get orphaned
// We handle this in a couple of ways:
// When draging an existing step we hide the [drop here] where they can't be dropped.
// If the user chooses to add an action where they can't exist we hide the goto step option in the action wizard
// If the user changes an existing action where a go to step can't exist we also hide the step option in the action wizard.

export const getAllowNoChildStep = (parentKey, stepKey, fromLabel, steps) => {
  // search in steps, if not find we assume it's a trigger which allows branches
  const parentStep = steps.find((s) => s.id === parentKey);

  // if a condition make sure the branch has no kids
  if (['condition', 'goal'].includes(parentStep?.type)) {
    const childSteps = getBranchChildrenStep([stepKey], fromLabel, steps);
    return childSteps.length === 0;
  }

  return true;
};
